import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, FormControl, Collapse, Typography, Autocomplete, Chip, Stack, useMediaQuery, useTheme } from "@mui/material";
import debounce from "lodash/debounce";
import { callBackendApiGet } from "../lib/api";
import { MinorUserContext } from "../context/UserNameProvider";
import { useSelector, useDispatch } from "react-redux";
import { setOtherMeds as updateOtherMeds } from "../redux/userSlice";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../lib/uiformatting";
import ContinueButton from "../components/ContinueButton";
import Question from "../components/Question";
import MoveButton from "../components/MoveButton";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

const debouncedSearch = debounce(async (searchTerm, callback) => {
    if (!searchTerm) {
        callback([]);
        return;
    }
    try {
        const response = await callBackendApiGet(
            `/medications?search=${encodeURIComponent(searchTerm)}`
        );
        const data = await response.json();
        callback(data.medications || []);
    } catch (error) {
        console.error("Error fetching medications:", error);
        callback([]);
    }
}, 300);

export default function OtherMeds() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const { isMinor, firstName } = useContext(MinorUserContext);
    const user = useSelector((state) => state.user);

    const [meds, setMeds] = useState(user.otherMeds.meds);
    const [error, setError] = useState("");
    const [yes, setYes] = useState(user.otherMeds.has);
    const [medList, setMedList] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleSearch = (searchTerm) => {
        setLoading(true);
        debouncedSearch(searchTerm, (results) => {
            setMedList(results);
            setLoading(false);
        });
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/body-part', navigate);
    };

    return (
        <Question
            onBack={handleBack}
            title={`${isMinor ? `Is ${firstName}` : "Are you"} currently on any other medications, unrelated to the skin issue?`}
            input={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Stack
                        spacing={2}
                        direction="column"
                        sx={{
                            width: "100%",
                            maxWidth: isDesktop ? "350px" : "100%",
                            mb: 2,
                            alignItems: "center"

                        }}
                    >
                        <MoveButton
                            onClick={() => {
                                setYes("y");
                            }}
                            title="Yes"
                            selected={yes === "y"}
                        />
                        <MoveButton
                            onClick={() => {
                                setYes("n");
                                setMeds([]);
                                setError("");
                                dispatch(updateOtherMeds({ has: "n", meds: [] }));
                                setPageAndDeferNavigate(dispatch, '/anything-else', navigate);
                            }}
                            title="No"
                            selected={yes === "n"}
                        />
                    </Stack>

                    <Collapse
                        in={yes === "y"}
                        mountOnEnter
                        unmountOnExit
                        sx={{
                            width: "100%",
                            maxWidth: isDesktop ? "600px" : "100%",
                            display: "flex",
                            justifyContent: "center"
                        }}
                    >
                        <FormControl
                            sx={{
                                width: "100%",
                                mt: "1rem",
                                maxWidth: isDesktop ? "600px" : "100%",
                            }}
                            required={yes === "y"}
                        >
                            <Typography id="med-label" variant="h6" sx={{ mb: "1rem", display: "flex", justifyContent: "center" }}>
                                Please provide names of the medications
                            </Typography>
                            <Typography variant="body1" sx={{ mb: "1rem", display: "flex", justifyContent: "center" }}>
                                This is so the physician can avoid any interactions with{" "}
                                {isMinor ? `${firstName}'s` : "your"} current medications.
                            </Typography>
                            <Autocomplete
                                aria-labelledby="med-label"
                                value={meds}
                                options={medList}
                                loading={loading}
                                onInputChange={(event, newInputValue) => {
                                    handleSearch(newInputValue);
                                }}
                                onChange={(e, newValue) => {
                                    if (newValue.length === 0) {
                                        setError("Please select at least one option.");
                                    } else {
                                        setError("");
                                    }
                                    setMeds(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="meds-select"
                                        label="Search here..."
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            ...textAreaSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        autoComplete="off"
                                        error={!!error}
                                        helperText={error}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            },
                                        }}
                                    />
                                )}
                                multiple
                                renderTags={(values, getTagProps) =>
                                    values.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            sx={{ fontSize: "1rem" }}
                                            label={option}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />
                        </FormControl>
                    </Collapse>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={() => {
                            dispatch(updateOtherMeds({ has: yes, meds: meds }));
                            setPageAndDeferNavigate(dispatch, '/anything-else', navigate);
                        }}
                        title="Continue"
                        disabled={yes.length === 0 || (yes === "y" && meds.length === 0)}
                        selected={(yes !== "" && yes === "n") || (yes === "y" && meds.length !== 0)}
                    />
                </Box>
            }
        />
    );
}